// globalErrorHandler.js
import { toast } from "react-toastify";
import { showGlobalErrorToast, showSuccessToast } from "./utils/toastUtils"; // use global error toast
import { TOAST_MESSAGES } from "./utils/toastMessages";
import { UpdateManager } from "./services/UpdateManager";

/**
 * Initialize global error handling
 * @param {axiosInstance} api - your Axios instance
 */
export const initGlobalErrorHandler = (api) => {
  // 1️⃣ Axios response interceptor
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // ✅ Skip showing toast if request is marked as silent
      if (originalRequest?.silent) {
        return Promise.reject(error);
      }

      //  No internet connection
      if (!navigator.onLine) {
        showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.NO_INTERNET);
        return Promise.reject(error);
      }

      // Auth errors (401/403) are handled centrally in `frontend/src/api.js`.
      // Avoid duplicating refresh/logout logic here, which can cause intermittent
      // failures when tokens are stored in sessionStorage only.
      if (error.response?.status === 401 || error.response?.status === 403) {
        return Promise.reject(error);
      }

      // Unstable connection (internet is on but no response)
      if (navigator.onLine && !error.response) {
        // showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.UNSTABLE_CONNECTION);
        return Promise.reject(error);
      }

      //  Too many requests
      if (error.response?.status === 429) {
        showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.TOO_MANY_REQUESTS);
        return Promise.reject(error);
      }

      //  Server errors 5xx
      if (error.response?.status >= 500) {
        // showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.SERVER_ERROR);
        return Promise.reject(error);
      }

      //  Maintenance mode
      if (
        error.response?.status === 503 &&
        error.response.headers["x-maintenance"]
      ) {
        showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.MAINTENANCE);
        return Promise.reject(error);
      }

      //  Request timeout
      if (error.code === "ECONNABORTED") {
        showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.REQUEST_TIMEOUT);
        return Promise.reject(error);
      }

      // Default error
      // const msg =
      //   error.response?.data?.message || TOAST_MESSAGES.GLOBAL.SERVER_ERROR;
      // showGlobalErrorToast(msg);

      return Promise.reject(error);
    }
  );

  // 2️⃣ Global JS errors
  window.onerror = (msg, url, line, col, error) => {
    showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.JS_ERROR);
    console.error(error || msg);

    const text = String(msg || error?.message || "");
    if (
      /ChunkLoadError/i.test(text) ||
      /Loading chunk \d+ failed/i.test(text) ||
      /Failed to fetch dynamically imported module/i.test(text) ||
      /Importing a module script failed/i.test(text)
    ) {
      try {
        UpdateManager.reportChunkLoadError({ message: text });
      } catch {}
    }
  };

  // 3️⃣ Unhandled promise rejections
  window.onunhandledrejection = (event) => {
    showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.PROMISE_REJECTION);
    console.error(event.reason);

    const reasonText = String(event?.reason?.message || event?.reason || "");
    if (
      /ChunkLoadError/i.test(reasonText) ||
      /Loading chunk \d+ failed/i.test(reasonText) ||
      /Failed to fetch dynamically imported module/i.test(reasonText) ||
      /Importing a module script failed/i.test(reasonText)
    ) {
      try {
        UpdateManager.reportChunkLoadError({ message: reasonText });
      } catch {}
    }
  };

  // 4️⃣ Offline / online events
  window.addEventListener("offline", () => {
    showGlobalErrorToast(TOAST_MESSAGES.GLOBAL.NO_INTERNET);
  });

  window.addEventListener("online", () => {
    toast.dismiss(); // remove offline toast
    showSuccessToast(TOAST_MESSAGES.GLOBAL.BACK_ONLINE);
  });
};
